import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from 'amazon-cognito-identity-js'
import JwtDecode from 'jwt-decode'
import Axios from 'axios'
import {
  VERIFICATION_CODE,
  SET_PASSWORD,
  SIGNED_IN
} from './utils'
import { handleLoginWithAzure } from './ActionHandlers'

import { reportAuthEvent } from 'utils/authEvents'

const API_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3990/'
    : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

// The origin the portal is served from. REACT_APP_PORTAL_URL lets a build run
// on any other hostname; without it the build targets production or the local
// dev server.
const PORTAL_URL =
  process.env.REACT_APP_PORTAL_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000'
    : 'https://portal.homepal.se')

const LOGOUT_URL = `${PORTAL_URL}/signout`

export const CALLBACK_URL = `${PORTAL_URL}/callback`

export const COGNITO_BASE_URL =
  process.env.REACT_APP_COGNITO_DOMAIN ||
  (process.env.NODE_ENV === 'development'
    ? 'https://dev-homepal.auth.eu-north-1.amazoncognito.com'
    : 'https://homepal.auth.eu-north-1.amazoncognito.com')

const COGNITO_LOGOUT_URL = `${COGNITO_BASE_URL}/logout?client_id=${process.env.REACT_APP_COGNITO_CLIENT_ID}&logout_uri=${LOGOUT_URL}`

class Storage {
  constructor() {
    this.cookies = {}
    this.settingCookies = {}
  }

  getItem(key) {
    return key in this.cookies ? this.cookies[key] : undefined
  }

  async setItem(key, value) {
    this.settingCookies[key] = true
    await Axios.post(
      `${API_URL}cookie`,
      { cookie: key, value: value },
      { withCredentials: true }
    )
    this.cookies[key] = value
    delete this.settingCookies[key]
  }

  removeItem(key) {
    delete this.cookies[key]
  }

  waitForCookies(callback) {
    const interval = setInterval(() => {
      if (Object.keys(this.settingCookies).length === 0) {
        clearInterval(interval)
        callback()
      }
    }, 300)
  }
}

const storage = new Storage()

const pool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
  Storage: storage
})

// The httpOnly cookies are cleared by the API, never by the portal host.
export const clearCookies = async () =>
  Axios.post(`${API_URL}sign-out`, {}, { withCredentials: true })

export const signOut = async () => {
  const user = pool.getCurrentUser()

  window.open(COGNITO_LOGOUT_URL, '_blank')

  await clearCookies()

  if (user) {
    await new Promise((resolve) => user.signOut(() => resolve()))
  }
}

const getCurrentUser = async () => {
  try {
    const response = await Axios.get(`${API_URL}last-user`, {
      withCredentials: true
    })

    if (
      response.data.email?.includes('@stockholm.se') ||
      response.data.email?.includes('@stockholmtest.se')
    ) {
      // Sign out so that we ensure that we revalidate from the IDP.
      await signOut()

      handleLoginWithAzure({ email: response.data.email })

      return new CognitoUser({
        Username: response.data.last_user,
        Pool: pool,
        Storage: storage
      })
    } else if (response.data.last_user) {
      const cognitoUser = new CognitoUser({
        Username: response.data.last_user,
        Pool: pool,
        Storage: storage
      })

      return cognitoUser
    }

    return null
  } catch (e) {
    return null
  }
}

export const getUserFromEmail = (email) => {
  return new CognitoUser({
    Storage: storage,
    Username: email,
    Pool: pool
  })
}

export const login = (params, rememberMe = false) => {
  const username = params.email.toLowerCase()

  const user = getUserFromEmail(username)

  const authDetails = new AuthenticationDetails({
    Username: username,
    Password: params.password
  })

  return new Promise((resolve) => {
    user.authenticateUser(authDetails, {
      onSuccess: () => {
        // onSuccess/onFailure is required, but we don't do anything
        if (rememberMe) {
          user.setDeviceStatusRemembered({
            onSuccess: () => {},
            onFailure: () => {}
          })
        } else {
          user.forgetDevice({ onSuccess: () => {}, onFailure: () => {} })
        }

        storage.waitForCookies(() => resolve({ type: SIGNED_IN }))
      },
      onFailure: () => {
        if (authDetails.username.includes('@')) {
          resolve({
            type: 'ERROR',
            error: 'Du har angivit en ogiltig email eller lösenord.'
          })
        } else {
          resolve({
            type: 'INVALID_EMAIL',
            error: 'För att logga in måste du använda en giltig email.'
          })
        }
      },
      newPasswordRequired: (data) => {
        delete data.email_verified

        resolve({ type: SET_PASSWORD, user })
      },
      mfaRequired: () => {
        resolve({ type: 'MFA_REQUIRED', user })
      },
      totpRequired: () => {
        resolve({ type: 'MFA_REQUIRED', user })
      }
    })
  })
}

export const sendMFACode = (user, mfaCode, rememberMe = false) => {
  return new Promise((resolve) => {
    user.sendMFACode(
      mfaCode,
      {
        onSuccess: () => {
          if (rememberMe) {
            user.setDeviceStatusRemembered({
              onSuccess: () => {},
              onFailure: () => {}
            })
          } else {
            user.forgetDevice({ onSuccess: () => {}, onFailure: () => {} })
          }

          storage.waitForCookies(() => resolve({ type: SIGNED_IN }))
        },
        onFailure: () => {
          resolve({
            type: 'ERROR',
            error: 'Ogiltig verifieringskod. Försök igen.'
          })
        }
      },
      'SOFTWARE_TOKEN_MFA'
    )
  })
}

export const saveSamlSession = async (authResponse) => {
  const decodedIdToken = JwtDecode(authResponse.id_token)
  const username = decodedIdToken['cognito:username']

  // The API verifies id_token and reads everything from it. groups,
  // provider_name and username are only for APIs deployed before that and
  // can be removed once it is out everywhere.
  const validateGroupBody = {
    id_token: authResponse.id_token,
    groups: decodedIdToken.zoneinfo,
    provider_name: decodedIdToken.identities[0].providerName,
    username
  }

  if (!username.includes('stockholm.se')) {
    await Axios.post(`${API_URL}validate-group`, validateGroupBody)
  } else {
    // Test why crash for stockholmshem
    try {
      await Axios.post(`${API_URL}validate-group`, validateGroupBody)
    } catch (e) {
      reportAuthEvent('FEDERATED_LOGIN_FAILED', decodedIdToken.email)
    }
  }

  const keyPrefix = `CognitoIdentityServiceProvider.${pool.getClientId()}`
  const idTokenKey = `${keyPrefix}.${username}.idToken`
  const accessTokenKey = `${keyPrefix}.${username}.accessToken`
  const refreshTokenKey = `${keyPrefix}.${username}.refreshToken`
  const lastUserKey = `${keyPrefix}.LastAuthUser`

  await pool.storage.setItem(accessTokenKey, authResponse.access_token)
  await pool.storage.setItem(idTokenKey, authResponse.id_token)
  await pool.storage.setItem(refreshTokenKey, authResponse.refresh_token)
  await pool.storage.setItem(lastUserKey, username)
}

export const getSession = () => {
  return new Promise(async (resolve, reject) => {
    const user = await getCurrentUser()

    if (!user) {
      return reject()
    }

    resolve()
  })
}

export const forgotPassword = (email) => {
  const user = getUserFromEmail(email.toLowerCase())

  return new Promise((resolve) =>
    user.forgotPassword({
      onFailure: () => {
        reportAuthEvent('FORGOT_PASSWORD_REQUESTED', email)
        resolve({ type: 'ERROR', error: 'Kunde inte hitta användaren' })
      },
      inputVerificationCode: () => {
        reportAuthEvent('FORGOT_PASSWORD_REQUESTED', email)
        resolve({ type: VERIFICATION_CODE, user })
      }
    })
  )
}

export const confirmPassword = (code, password, user) => {
  return new Promise((resolve) =>
    user.confirmPassword(code, password, {
      onSuccess: async () => {
        await login({ password, email: user.username })
        storage.waitForCookies(() => resolve({ type: SIGNED_IN }))
      },
      onFailure: () => {
        resolve({ type: 'ERROR', error: 'Något gick fel..' })
      }
    })
  )
}

export const setNewPasswordChallenge = (password, user, clientMetadata) => {
  return new Promise((resolve, reject) =>
    user.completeNewPasswordChallenge(
      password,
      {},
      {
        onSuccess: () => {
          storage.waitForCookies(() => resolve({ type: SIGNED_IN }))
        },
        onFailure: () => {
          reject()
        }
      },
      {
        ...clientMetadata
      }
    )
  )
}
